import {
  CoordinateResult,
  Point,
  ResultType,
  Triangle,
} from '../../geometric-types';

export enum BoundingBoxPosition {
  MIN_X = 'min-x',
  MIN_Y = 'min-y',
  MIN_Z = 'min-z',
  MAX_X = 'max-x',
  MAX_Y = 'max-y',
  MAX_Z = 'max-z',
}

interface BoundingBoxPoints {
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
  minXPoint: [number, number, number];
  minYPoint: [number, number, number];
  minZPoint: [number, number, number];
  maxXPoint: [number, number, number];
  maxYPoint: [number, number, number];
  maxZPoint: [number, number, number];
}

function findBoundingBoxPoints(triangles: Triangle[]): BoundingBoxPoints {
  const allVertices = triangles.flatMap((triangle) => triangle);

  const result: BoundingBoxPoints = {
    minX: Infinity,
    minY: Infinity,
    minZ: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
    maxZ: -Infinity,
    minXPoint: [0, 0, 0],
    minYPoint: [0, 0, 0],
    minZPoint: [0, 0, 0],
    maxXPoint: [0, 0, 0],
    maxYPoint: [0, 0, 0],
    maxZPoint: [0, 0, 0],
  };

  for (const vertex of allVertices) {
    const [x, y, z] = vertex;

    if (x < result.minX) {
      result.minX = x;
      result.minXPoint = vertex;
    }
    if (y < result.minY) {
      result.minY = y;
      result.minYPoint = vertex;
    }
    if (z < result.minZ) {
      result.minZ = z;
      result.minZPoint = vertex;
    }
    if (x > result.maxX) {
      result.maxX = x;
      result.maxXPoint = vertex;
    }
    if (y > result.maxY) {
      result.maxY = y;
      result.maxYPoint = vertex;
    }
    if (z > result.maxZ) {
      result.maxZ = z;
      result.maxZPoint = vertex;
    }
  }

  return result;
}

// Get the bounding box of a set of triangles and a specific position
export function calculateBoundingBox(
  triangles: Triangle[],
  boundingBoxPosition: BoundingBoxPosition,
  globalOffset: Point
): CoordinateResult {
  const points = findBoundingBoxPoints(triangles);

  switch (boundingBoxPosition) {
    case BoundingBoxPosition.MIN_X:
      return {
        type: ResultType.COORDINATE,
        point: points.minXPoint,
        value: points.minX + globalOffset[0],
      };
    case BoundingBoxPosition.MIN_Y:
      return {
        type: ResultType.COORDINATE,
        point: points.minYPoint,
        value: points.minY + globalOffset[1],
      };
    case BoundingBoxPosition.MIN_Z:
      return {
        type: ResultType.COORDINATE,
        point: points.minZPoint,
        value: points.minZ + globalOffset[2],
      };
    case BoundingBoxPosition.MAX_X:
      return {
        type: ResultType.COORDINATE,
        point: points.maxXPoint,
        value: points.maxX + globalOffset[0],
      };
    case BoundingBoxPosition.MAX_Y:
      return {
        type: ResultType.COORDINATE,
        point: points.maxYPoint,
        value: points.maxY + globalOffset[1],
      };
    case BoundingBoxPosition.MAX_Z:
      return {
        type: ResultType.COORDINATE,
        point: points.maxZPoint,
        value: points.maxZ + globalOffset[2],
      };
  }
}
